import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Sparkles,
  Check,
  Trash2,
  User
} from 'lucide-react';
import { PageTemplate } from '../../../types/pageBuilder';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { allTemplates as baseTemplates, categories as baseCategories } from '../templates/templateData';
import { getUserTemplates, deleteUserTemplate, UserTemplate } from '../../../services/userTemplateService';
import { getTranslatedTemplates } from '../templates/templateHelper';

// Category translations
const categoryTranslations: Record<string, string> = {
  'all': 'All',
  'custom': 'My Templates',
  'saas': 'SaaS',
  'lead-magnet': 'Lead Magnet',
  'webinar': 'Webinar',
  'product-launch': 'Launch',
  'agency': 'Agency/Services',
  'ecommerce': 'E-commerce',
  'pet': 'Pet/Veterinary'
};

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: PageTemplate) => void;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  isOpen,
  onClose,
  onSelectTemplate
}) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [userTemplates, setUserTemplates] = useState<UserTemplate[]>([]);

  // Get translated templates based on current language
  const templates = useMemo(() => {
    return getTranslatedTemplates(baseTemplates, i18n.language);
  }, [i18n.language]);

  // Translate categories
  const categories = useMemo(() => {
    const isEn = i18n.language === 'en' || i18n.language === 'en-US';
    return baseCategories.map(cat => ({
      ...cat,
      label: isEn ? (categoryTranslations[cat.id] || cat.label) : cat.label
    }));
  }, [i18n.language]);

  // Load user templates
  useEffect(() => {
    if (isOpen) {
      setUserTemplates(getUserTemplates());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDeleteUserTemplate = (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    if (window.confirm(t('pageBuilder.templates.deleteConfirm', 'Tem certeza que deseja excluir este template?'))) {
      deleteUserTemplate(templateId);
      setUserTemplates(getUserTemplates());
      if (selectedTemplate === templateId) {
        setSelectedTemplate(null);
      }
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
                         template.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredUserTemplates = userTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
                         template.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || activeCategory === 'custom' || template.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSelect = () => {
    if (selectedTemplate) {
      // Handle blank template
      if (selectedTemplate === 'blank') {
        onSelectTemplate({ id: 'blank', name: 'Blank', description: '', thumbnail: '', category: 'blank' });
        onClose();
        return;
      }
      // First check user templates
      const userTemplate = userTemplates.find(t => t.id === selectedTemplate);
      if (userTemplate) {
        onSelectTemplate(userTemplate);
        onClose();
        return;
      }
      // Then check system templates
      const template = templates.find(t => t.id === selectedTemplate);
      if (template) {
        onSelectTemplate(template);
        onClose();
      }
    }
  };

  // Add custom category if not in list
  const allCategories = [
    { id: 'custom', label: t('pageBuilder.templates.myTemplates', 'Meus Templates') },
    ...categories
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 hidden sm:block" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-none sm:rounded-2xl shadow-2xl w-full max-w-5xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('pageBuilder.templates.title', 'Escolha um Template')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('pageBuilder.templates.subtitle', 'Comece com um design profissional e personalize a vontade')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search and Filter */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-full sm:w-64 flex-shrink-0">
              <Input
                placeholder={t('pageBuilder.templates.search', 'Buscar templates...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="w-5 h-5" />}
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1">
              {allCategories.map((category) => {
                const count = category.id === 'custom' ? userTemplates.length : 0;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      activeCategory === category.id
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {category.id === 'custom' && <User className="w-4 h-4" />}
                    {category.label}
                    {category.id === 'custom' && count > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                        activeCategory === category.id
                          ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Blank option */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t('pageBuilder.templates.startBlank', 'Comecar do Zero')}</h3>
            <button
              onClick={() => setSelectedTemplate('blank')}
              className={`w-48 h-36 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-all ${
                selectedTemplate === 'blank'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedTemplate === 'blank'
                  ? 'bg-indigo-100 dark:bg-indigo-900/50'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <Sparkles className={`w-5 h-5 ${
                  selectedTemplate === 'blank'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-400'
                }`} />
              </div>
              <span className={`font-medium ${
                selectedTemplate === 'blank'
                  ? 'text-indigo-700 dark:text-indigo-300'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {t('pageBuilder.templates.blankPage', 'Pagina em Branco')}
              </span>
            </button>
          </div>

          {/* User Templates */}
          {(activeCategory === 'all' || activeCategory === 'custom') && filteredUserTemplates.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('pageBuilder.templates.myTemplates', 'Meus Templates')} ({filteredUserTemplates.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredUserTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`group relative overflow-hidden rounded-xl border-2 transition-all ${
                      selectedTemplate === template.id
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 overflow-hidden flex items-center justify-center">
                      <div className="text-center px-4">
                        <User className="w-8 h-8 text-indigo-500 mx-auto mb-2" />
                        <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{t('pageBuilder.templates.savedTemplate', 'Template Salvo')}</span>
                      </div>
                      {selectedTemplate === template.id && (
                        <div className="absolute inset-0 bg-indigo-500/10 flex items-center justify-center">
                          <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDeleteUserTemplate(e, template.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title={t('pageBuilder.templates.deleteTemplate', 'Excluir template')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    {/* Info */}
                    <div className="p-3 text-left">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {template.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {template.description || t('pageBuilder.templates.customTemplate', 'Template customizado')}
                      </p>
                      <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1">
                        {new Date(template.createdAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'pt-BR')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Empty user templates state */}
          {activeCategory === 'custom' && filteredUserTemplates.length === 0 && (
            <div className="mb-8 text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('pageBuilder.templates.noTemplates', 'Nenhum template salvo')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('pageBuilder.templates.noTemplatesDesc', 'Crie uma landing page e salve como template para usar depois')}
              </p>
            </div>
          )}

          {/* System Templates */}
          {activeCategory !== 'custom' && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                {t('pageBuilder.templates.readyTemplates', 'Templates Prontos')} ({filteredTemplates.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`group relative overflow-hidden rounded-xl border-2 transition-all ${
                      selectedTemplate === template.id
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <img
                        src={template.thumbnail}
                        alt={template.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {selectedTemplate === template.id && (
                        <div className="absolute inset-0 bg-indigo-500/10 flex items-center justify-center">
                          <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3 text-left">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {template.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {template.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('pageBuilder.templates.noResults', 'Nenhum template encontrado')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('pageBuilder.templates.noResultsDesc', 'Tente ajustar os filtros ou buscar por outro termo')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {selectedTemplate ? (
              selectedTemplate === 'blank'
                ? t('pageBuilder.templates.blankSelected', 'Pagina em branco selecionada')
                : t('pageBuilder.templates.templateSelected', 'Template "{{name}}" selecionado', {
                    name: userTemplates.find(t => t.id === selectedTemplate)?.name ||
                          templates.find(t => t.id === selectedTemplate)?.name
                  })
            ) : (
              t('pageBuilder.templates.selectToStart', 'Selecione um template para comecar')
            )}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={handleSelect}
              disabled={!selectedTemplate}
              leftIcon={<Check className="w-4 h-4" />}
            >
              {t('pageBuilder.templates.useTemplate', 'Usar Template')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateGallery;
